import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Truck, SignIn, GoogleLogo, EnvelopeSimpleOpen } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const DEMO = [
 // { label: "Super Admin", email: "admin@courier.com", password: "admin123" },
//{ label: "Company Admin", email: "company@swift.com", password: "password123" },
//  { label: "Dispatcher", email: "dispatcher@swift.com", password: "password123" },
//  { label: "Viewer", email: "user@swift.com", password: "password123" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // When backend rejects login with `email_not_verified`, we surface an inline
  // notice with a "Resend verification email" button so the user isn't stuck.
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setUnverifiedEmail(null);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u?.role === "driver" ? "/driver/portal" : "/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Structured detail signalling unverified email — show inline UI.
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setUnverifiedEmail(detail.email || email.toLowerCase());
        toast.error(detail.message || "Please verify your email before signing in.");
      } else if (detail && typeof detail === "object" && detail.code === "wrong_tenant") {
        // User landed on a courier's white-label portal with credentials for a
        // different tenant. Tell them clearly which portal they need.
        toast.error(detail.message || "Wrong portal for this account.", { duration: 6000 });
      } else {
        const msg = detail
          ? formatApiErrorDetail(detail)
          : err?.message
            ? `Network error: ${err.message}. Please check your connection and try again.`
            : "Login failed. Please try again.";
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!unverifiedEmail) return;
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: unverifiedEmail });
      toast.success("Verification email sent. Check your inbox.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not resend right now.");
    } finally {
      setResending(false);
    }
  };

  const fillDemo = (d) => {
    setEmail(d.email);
    setPassword(d.password);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <>
    <Toaster position="top-right" />
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F4F4F5]" data-testid="login-page">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
           <a href="https://kanal.cyrusfieldtech.com"> <img src="/brand/kanal-logo.svg" alt="Kanal" className="h-11 w-11" /></a>
            <div>
              <div className="font-semibold text-lg" style={{ fontFamily: "Work Sans" }}>
                Kanal
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                Courier Platform · <a href="https://cyrusfieldtech.com" target="_blank" rel="noreferrer" className="hover:text-zinc-700">By Cyrus Field Tech</a>
              </div>
            </div>
          </div>

          <h1
            className="text-3xl sm:text-4xl font-semibold tracking-tight mb-2"
            style={{ fontFamily: "Work Sans" }}
          >
            Sign in
          </h1>
          <p className="text-sm text-zinc-500 mb-8">
            Enter your credentials to access your workspace.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Work Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                data-testid="email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="password-input"
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full bg-[#002BF6] hover:bg-[#0021C7] text-white h-10"
            >
              {submitting ? "Signing in..." : (
                <>
                  <SignIn size={16} className="mr-2" weight="bold" />
                  Sign in
                </>
              )}
            </Button>

            {unverifiedEmail && (
              <div
                className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
                data-testid="email-not-verified-notice"
              >
                <div className="flex items-start gap-2">
                  <EnvelopeSimpleOpen size={18} weight="bold" className="text-amber-700 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-amber-900">
                      Verify your email to continue
                    </div>
                    <div className="text-xs text-amber-800 mt-1">
                      We&apos;ve sent a verification link to <span className="font-mono">{unverifiedEmail}</span>.
                      Click the link in that email, then sign in again.
                    </div>
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resending}
                      data-testid="resend-verification-btn"
                      className="mt-2 inline-flex items-center text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700 disabled:opacity-60"
                    >
                      {resending ? "Sending…" : "Resend verification email"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-zinc-200" />
            <span className="text-[11px] uppercase tracking-wider text-zinc-400">or</span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>

        

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-zinc-900" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>

          <div className="mt-8 border-t border-zinc-200 pt-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">
              Ask to try a demo account. Write us at <a href="mailto:support@cyrusfieldtech.com">support@cyrusfieldtech.com</a>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => fillDemo(d)}
                  data-testid={`demo-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-left px-3 py-2 rounded-md border border-zinc-200 bg-white hover:border-[#002BF6] hover:bg-zinc-50 transition-all duration-200"
                >
                  <div className="text-xs font-semibold text-zinc-900">{d.label}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{d.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right: hero */}
      <div
        className="hidden md:block relative"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(9,9,11,0.92) 0%, rgba(0,43,246,0.55) 100%), url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/60 mb-3">
            Platform / Logistics
          </div>
          <h2
            className="text-4xl font-semibold leading-tight mb-4"
            style={{ fontFamily: "Work Sans" }}
          >
            The operations control room
            <br />
            for modern couriers.
          </h2>
          <p className="text-white/70 max-w-md text-sm leading-relaxed">
            Manage tenants, dispatchers, packages and invoices — all from a single,
            multi-tenant dashboard built for speed.
          </p>
          <div className="mt-8 flex gap-8">
            <Stat label="Uptime" value="99.99%" />
            <Stat label="Tenants" value="250+" />
            <Stat label="Packages / mo" value="1.2M" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-2xl font-semibold" style={{ fontFamily: "Work Sans" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
    </div>
  );
}
